import React, { useCallback, useEffect, useState } from 'react';

import { FlexBox } from 'ml-rubiks-styleguide/src/box';

import { useCustomerData } from '../../contexts/customer-data-context';
import { useFeatureFlags } from '../../contexts/feature-flags-context';
import { useI18n } from '../../contexts/i18n-context';
import { usePaymentErrorContext } from '../../contexts/payment-error-context';
import { usePaymentState } from '../../contexts/payment-state-context';
import { PaymentProcessor } from '../../core/payment-processor';
import { PaymentConfig } from '../../types';
import { validateConfig } from '../../utils/config-validation';
import {
    registerWidgetAPI,
    unregisterWidgetAPI
} from '../../utils/root-registry';
import { LoadingMessage } from '../common';
import { PaymentWidget } from './payment-widget';

interface PaymentWidgetWrapperProps {
    config: PaymentConfig;
    container: HTMLElement;
}

export const PaymentWidgetWrapper: React.FC<PaymentWidgetWrapperProps> = ({
    config,
    container
}) => {
    const { t } = useI18n();
    const { flags } = useFeatureFlags();
    const { addError } = usePaymentErrorContext();
    const { triggerSubmit, getPaymentState } = usePaymentState();
    const { updateCustomerFields } = useCustomerData();
    const [paymentProcessor, setPaymentProcessor] =
        useState<PaymentProcessor | null>(null);
    const [isInitializing, setIsInitializing] = useState(true);
    const [isProcessingRedirect, setIsProcessingRedirect] = useState(false);
    const [redirectSucceeded, setRedirectSucceeded] = useState(false);

    const widgetApi = React.useMemo(
        () => ({
            submitPayment: triggerSubmit,
            getPaymentState,
            updateCustomerFields
        }),
        [triggerSubmit, getPaymentState, updateCustomerFields]
    );

    const initializePaymentWidget = useCallback(
        async (signal: AbortSignal) => {
            setIsInitializing(true);
            try {
                validateConfig(config);

                const processor = new PaymentProcessor();
                processor.configure(config);

                processor.setGlobalErrorHandler((error) => {
                    if (!signal.aborted) {
                        addError(error);
                    }
                });

                try {
                    await processor.handleRedirect(
                        () => {
                            if (!signal.aborted) {
                                setIsProcessingRedirect(true);
                            }
                        },
                        () => {
                            if (!signal.aborted) {
                                setIsProcessingRedirect(false);
                            }
                        },
                        () => {
                            if (!signal.aborted) {
                                setRedirectSucceeded(true);
                            }
                        }
                    );
                } catch (redirectError) {
                    if (!signal.aborted) {
                        setIsProcessingRedirect(false);
                        addError(redirectError);
                    }
                }

                if (signal.aborted) {
                    return;
                }

                try {
                    const sessionDetailsData =
                        await processor.getUserPaymentSessionDetails();
                    if (!signal.aborted) {
                        processor.setSessionDetails(sessionDetailsData);
                    }
                } catch (sessionError) {
                    if (!signal.aborted) {
                        addError(sessionError);
                    }
                }

                if (!signal.aborted) {
                    setPaymentProcessor(processor);
                }
            } catch (err) {
                if (!signal.aborted) {
                    addError(err);
                    const processor = new PaymentProcessor();
                    setPaymentProcessor(processor);
                }
            } finally {
                if (!signal.aborted) {
                    setIsInitializing(false);
                }
            }
        },
        [config, addError]
    );

    useEffect(() => {
        const abortController = new AbortController();
        void initializePaymentWidget(abortController.signal);

        return () => {
            abortController.abort();
        };
    }, [initializePaymentWidget]);

    useEffect(() => {
        registerWidgetAPI(widgetApi, container);

        return () => {
            unregisterWidgetAPI(container);
        };
    }, [widgetApi, container]);

    if (isInitializing) {
        return (
            <FlexBox padding={8} hAlign="center" minWidth="100%">
                <LoadingMessage
                    message={t(
                        'upc.loading.initializing',
                        'Initializing payment widget...'
                    )}
                />
            </FlexBox>
        );
    }

    if (isProcessingRedirect) {
        return (
            <FlexBox padding={8} hAlign="center" minWidth="100%">
                <LoadingMessage
                    message={t(
                        'upc.loading.processing',
                        'Processing payment...'
                    )}
                />
            </FlexBox>
        );
    }

    if (redirectSucceeded && !(flags.showPaymentInstrumentList ?? true)) {
        return null;
    }

    if (!paymentProcessor) {
        return null;
    }

    return <PaymentWidget config={config} paymentProcessor={paymentProcessor} />;
};
